// The plate shared by every modal moment — celebration, branch pick, run over,
// costs, feedback.
//
// **A styled MUI `Dialog`, not a hand-rolled overlay.** The focus trap, the
// portal, `Escape`, restoring focus on close and `aria-modal` are the parts
// that are hard to get right and boring to re-derive; what the kit changes is
// the paper, which becomes a `Panel` plate, and the backdrop, which becomes
// screen-mock's `#pause` glass.
//
// **No z-index here.** MUI's own ladder carries the dialog, and that ladder is
// set from `TOKENS.z` in `theme.ts` — a literal at this call site would be a
// layer nobody can order against the others (PLAN-UI §28.1).

import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";

export const Modal = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backgroundColor: `color-mix(in srgb, ${theme.kit.color.shadow} 85%, transparent)`,
    backdropFilter: "blur(5px)"
  },
  "& .MuiDialog-paper": {
    background: theme.kit.color.panel,
    // MUI paints an elevation tint over a dark paper; the plate is one flat
    // surface with an edge, and the theme already sets elevation 0.
    backgroundImage: "none",
    boxShadow: "none",
    border: `2px solid ${theme.kit.color.line}`,
    borderRadius: theme.kit.radius.md,
    color: theme.kit.color.ink,
    fontFamily: theme.kit.font.body
  }
}));

// The three parts every modal has, and the reason they are here rather than at
// each call site: the padding is asymmetric — a title's bottom gap is the
// body's top gap — so five dialogs writing it by hand is five dialogs whose
// spacing disagrees by two pixels. Replaces `DialogTitle`/`Content`/`Actions`,
// which carry MUI's own paddings and a divider system.

export const ModalTitle = styled("h2")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 22,
  lineHeight: 1.15,
  color: theme.kit.color.ink,
  margin: 0,
  padding: `${theme.kit.space.lg}px ${theme.kit.space.xl}px ${theme.kit.space.sm}px`
}));

export const ModalBody = styled("div")(({ theme }) => ({
  padding: `${theme.kit.space.sm}px ${theme.kit.space.xl}px ${theme.kit.space.lg}px`,
  overflow: "auto"
}));

export const ModalActions = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: theme.kit.space.sm,
  padding: `0 ${theme.kit.space.xl}px ${theme.kit.space.lg}px`
}));
